import TIM from "tim-js-sdk";
import { post } from "@/util/http";
import { getChannel } from "@/util/channel";
import { store } from "@/store/index.js";
import Vue from "vue";
import { format } from "@/util/time";
import { aesDecode, aesEncode } from "@/util/aes";
import { decodePicture } from "@/util/decodePicture";

export let tim = null;
const TIM_SDK_APP_ID_KEY = "tim_sdkappid";
const TIM_USER_SIG_CACHE_KEY = "tim_user_sig_cache";
/** 前端复用 sig，避免后端 getUserSig 一分钟加闸被打爆；sig 本身有效期通常远长于此 */
const USER_SIG_CACHE_TTL_MS = 50 * 60 * 1000;
let currentSdkAppId = null;
let timInitPromise = null;
let getUserSigPromise = null;
/** 当前已成功 login 的 TIM userID，用于跳过重复登录/重复拉 sig */
let loggedInUserId = null;
/** 当前 TIM 登录对应的业务身份：登录用户 id 或 guest，切号时必须一致才能复用 */
let loggedInBizUserKey = null;

/** 需要登录 IM 的路由（直播间聊天室、私聊、相关页） */
const TIM_ROUTE_NAMES = [
  "liveMatchesTwo",
  "privateChat",
  "anchorReplay",
  "anchorInfo",
  "scheduleDetail",
];

/**
 * 首页/列表等非 IM 场景不登录，避免刷量抬高 IM DAU。
 * 进入直播间、私聊等页面再 initTim。
 */
export function routeNeedsTim(route) {
  return TIM_ROUTE_NAMES.includes(route?.name);
}

function getBizUserKey() {
  return store.state.userInfo?.id ? String(store.state.userInfo.id) : "guest";
}

function readSigCache() {
  try {
    const raw = sessionStorage.getItem(TIM_USER_SIG_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    if (!cache?.id || !cache?.sig || !cache?.sdkAppId) return null;
    if (cache.expireAt && Date.now() > cache.expireAt) return null;
    // 必须同一业务账号，避免切号后串用上一个人的 sig
    if (String(cache.bizUserKey || "") !== getBizUserKey()) return null;
    return cache;
  } catch (e) {
    return null;
  }
}

function writeSigCache({ id, sig, sdkAppId }) {
  if (!id || !sig || !sdkAppId) return;
  try {
    sessionStorage.setItem(
      TIM_USER_SIG_CACHE_KEY,
      JSON.stringify({
        id: String(id),
        sig: String(sig),
        sdkAppId: String(sdkAppId),
        bizUserKey: getBizUserKey(),
        expireAt: Date.now() + USER_SIG_CACHE_TTL_MS,
      })
    );
  } catch (e) {
    console.warn("[getUserSig] 写入缓存失败", e);
  }
}

function clearSigCache() {
  try {
    sessionStorage.removeItem(TIM_USER_SIG_CACHE_KEY);
  } catch (e) {
    /* ignore */
  }
}

/** 登录/退出/登录超时：清掉 sig 缓存，防止切号串号 */
export function clearTimUserSigCache() {
  clearSigCache();
  loggedInUserId = null;
  loggedInBizUserKey = null;
}

export function initTim() {
  if (timInitPromise) return timInitPromise;

  timInitPromise = initTimCore().finally(() => {
    timInitPromise = null;
  });

  return timInitPromise;
}

async function initTimCore() {
  // 防御性检查：确保 appConfig 和 im_sdkappid 已加载
  const appConfig = store.state?.appConfig;
  const sdkAppId = appConfig?.im_sdkappid ? String(appConfig.im_sdkappid) : "";
  
  if (!sdkAppId) {
    console.warn("[initTim] appConfig 或 im_sdkappid 尚未加载，跳过 TIM 初始化");
    return;
  }
  
  console.log("[initTim] SDKAppID:", sdkAppId);

  const cachedSdkAppId = localStorage.getItem(TIM_SDK_APP_ID_KEY) || "";
  const instanceSdkAppIdChanged = tim && currentSdkAppId !== sdkAppId;
  const cachedSdkAppIdChanged = cachedSdkAppId && cachedSdkAppId !== sdkAppId;
  const shouldRefreshSig = instanceSdkAppIdChanged || cachedSdkAppIdChanged || (!cachedSdkAppId && store.state.userInfo.id);
  const bizUserKey = getBizUserKey();
  // 业务账号变了（游客↔登录用户 / A↔B）：必须重登，不能复用旧 TIM 会话
  const bizUserChanged = loggedInBizUserKey && loggedInBizUserKey !== bizUserKey;

  // 已登录同一 SDK、同一业务账号，且无需刷新 sig：进房重复 initTim 直接跳过
  if (
    tim &&
    currentSdkAppId === sdkAppId &&
    loggedInUserId &&
    loggedInBizUserKey === bizUserKey &&
    !shouldRefreshSig &&
    !bizUserChanged
  ) {
    return;
  }

  if (instanceSdkAppIdChanged || bizUserChanged) {
    if (bizUserChanged) {
      console.warn("[initTim] 业务账号切换，重建 TIM 实例:", loggedInBizUserKey, "=>", bizUserKey);
      clearSigCache();
    } else {
      console.warn("[initTim] SDKAppID 已变化，重建 TIM 实例:", currentSdkAppId, "=>", sdkAppId);
    }
    await destroyTimInstance();
  }

  if (tim == null) {
    createTimInstance(sdkAppId);
  }

  if (!tim) return;

  const loginSuccess = await loginTim({
    forceRefreshSig: shouldRefreshSig || bizUserChanged,
  });
  if (loginSuccess) {
    localStorage.setItem(TIM_SDK_APP_ID_KEY, sdkAppId);
  }
}

function createTimInstance(sdkAppId) {
  let options = {
    SDKAppID: sdkAppId,
  };
  // 创建 SDK 实例，`TIM.create()`方法对于同一个 `SDKAppID` 只会返回同一份实例
  tim = TIM.create(options); // SDK 实例通常用 tim 表示
    
  // 检查 tim 是否创建成功
  if (!tim) {
    console.error("[initTim] TIM.create 返回 null，初始化失败");
    return;
  }

  currentSdkAppId = sdkAppId;
    
  // tim.setLogLevel(0); // 普通级别，日志量较多，接入时建议使用
  tim.setLogLevel(1); // release 级别，SDK 输出关键信息，生产环境时建议使用
  tim.on(TIM.EVENT.SDK_READY, timOnSdkReady);
  tim.on(TIM.EVENT.MESSAGE_RECEIVED, timOnMessageReceived);
  tim.on(TIM.EVENT.MESSAGE_MODIFIED, timOnMessageModified);
  tim.on(TIM.EVENT.MESSAGE_REVOKED, timOnMessageRevoked);
  tim.on(TIM.EVENT.ERROR, timOnError);
  tim.on(TIM.EVENT.KICKED_OUT, onKickedOut);
}

async function destroyTimInstance() {
  store.commit("toggleTimReady", false);
  store.commit("changeLoginRepData", {});
  loggedInUserId = null;
  loggedInBizUserKey = null;

  if (!tim) {
    currentSdkAppId = null;
    return;
  }

  try {
    await tim.destroy();
    console.log("sdk destroyed");
  } catch (error) {
    console.warn("[initTim] TIM destroy 失败:", error);
  } finally {
    tim = null;
    currentSdkAppId = null;
  }
}

function timOnMessageRevoked(event) {
  const revokedList = event?.data;
  if (!Array.isArray(revokedList) || revokedList.length === 0) return;
  revokedList.forEach((message) => {
    const convType = message?.conversationType;
    const msgId = message?.ID;
    const seq = message?.sequence;
    if (convType === TIM.TYPES.CONV_C2C) {
      const msgIdOrSeq = msgId ?? seq;
      if (msgIdOrSeq != null && msgIdOrSeq !== "") {
        store.commit("removePrivateMessageBySeq", msgIdOrSeq);
      }
    } else if (convType === TIM.TYPES.CONV_GROUP) {
      const groupId = store.state.fanGroup?.fans_group_id || store.state.fanGroup?.group_id || "";
      const toGroupId = String(message?.to ?? "").trim();
      if (groupId && toGroupId === String(groupId)) {
        const groupMsg = store.state.groupMessageList;
        console.log("timOnMessageRevoked 撤回 seq:", seq, "msgId:", msgId);
        // 同时匹配 seq、id、msg_id 字段，确保能正确移除被撤回的消息
        const arr = groupMsg.filter((e) => {
          if (e.chatType === 3) return true;
          const eSeq = e.seq || e.id || e.msg_id;
          const matchSeq = seq && eSeq == seq;
          const matchId = msgId && (e.id == msgId || e.msg_id == msgId || e.seq == msgId);
          return !matchSeq && !matchId;
        });
        store.commit("changeGroupMessageList", arr);
      }
    }
  });
}

function timOnError(event) {
  console.log("timOnError", event.data);
}

//被踢下线
function onKickedOut(event) {
  // Vue.prototype.$message.error("聊天室已离线，请刷新重新登录");

  console.log("被踢下线", event.data.type);
  clearSigCache();
  destroyTimInstance().then(() => {
    tim = null;
    currentSdkAppId = null;
    store.commit("popupLogin");
  });
}

async function loginTim({ forceRefreshSig = false } = {}) {
  const bizUserKey = getBizUserKey();

  // 已登录且业务身份一致、不强制刷新：直接复用
  if (!forceRefreshSig && loggedInUserId && loggedInBizUserKey === bizUserKey) {
    // 登录用户还要保证 TIM uid 就是当前业务 uid
    if (!store.state.userInfo.id || String(loggedInUserId) === String(store.state.userInfo.id)) {
      return true;
    }
  }

  if (!forceRefreshSig && store.state.userInfo.id && store.state.userInfo.sig) {
    return await userLoginTim(String(store.state.userInfo.id), store.state.userInfo.sig);
  }

  // 游客 / 无本地 sig：优先用「当前业务身份」下的 session 缓存
  if (!forceRefreshSig) {
    const cache = readSigCache();
    if (cache && String(cache.sdkAppId) === String(currentSdkAppId)) {
      const ok = await userLoginTim(cache.id, cache.sig);
      if (ok) return true;
      clearSigCache();
    }
  }

  return await getUserSig({ syncLocalUserInfo: !!store.state.userInfo.id });
}

async function getUserSig({ syncLocalUserInfo = false } = {}) {
  if (getUserSigPromise) return getUserSigPromise;

  getUserSigPromise = (async () => {
    try {
      const { data } = await post({ url: "/api/tencentIm/getUserSig" });
      console.log(data);
      if (data.status == 0 && data.data?.id && data.data?.sig) {
        writeSigCache({
          id: data.data.id,
          sig: data.data.sig,
          sdkAppId: currentSdkAppId,
        });
        const loginSuccess = await userLoginTim(data.data.id, data.data.sig);
        if (loginSuccess && syncLocalUserInfo && store.state.userInfo.id && data.data?.sig) {
          const localUserInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
          const userInfo = {
            ...localUserInfo,
            ...store.state.userInfo,
            sig: data.data.sig,
          };
          localStorage.setItem("userInfo", JSON.stringify(userInfo));
          store.commit("changeUserInfo", userInfo);
        }
        return loginSuccess;
      }

      // 被加闸时尽量用缓存 sig 顶住
      const msg = data?.msg || data?.err_msg || "";
      if (String(msg).indexOf("频繁") > -1) {
        const cache = readSigCache();
        if (cache && String(cache.sdkAppId) === String(currentSdkAppId)) {
          console.warn("[getUserSig] 请求过于频繁，改用缓存 sig");
          return await userLoginTim(cache.id, cache.sig);
        }
      }
      return false;
    } catch (e) {
      console.warn("[getUserSig] 请求失败", e);
      const cache = readSigCache();
      if (cache && String(cache.sdkAppId) === String(currentSdkAppId)) {
        console.warn("[getUserSig] 接口异常，改用缓存 sig");
        return await userLoginTim(cache.id, cache.sig);
      }
      return false;
    } finally {
      getUserSigPromise = null;
    }
  })();

  return getUserSigPromise;
}

function userLoginTim(uid, sig) {
  if (!tim) return Promise.resolve(false);
  if (!uid || !sig) return Promise.resolve(false);
  const bizUserKey = getBizUserKey();
  if (
    loggedInUserId &&
    String(loggedInUserId) === String(uid) &&
    loggedInBizUserKey === bizUserKey
  ) {
    return Promise.resolve(true);
  }
  // , channel: channels 
  let promise = tim.login({ userID: String(uid), userSig: sig, channel: getChannel() });
  return promise
    .then((imResponse) => {
      console.log(imResponse.data, "登录TIM成功"); // 登录成功
      if (imResponse.data.repeatLogin === true) {
        console.log(imResponse.data.errorInfo, "重复TIM登录");
      }
      loggedInUserId = String(uid);
      loggedInBizUserKey = bizUserKey;
      return true;
    })
    .catch((imError) => {
      console.warn("login error:", imError, imError.code); // 登录失败的相关信息
      loggedInUserId = null;
      loggedInBizUserKey = null;
      if (imError.code === 70001) {
        clearSigCache();
        store.commit("popupLogin");
      }
      return false;
    });
}
function timOnSdkReady() {
  store.commit("toggleTimReady", true);
  console.log("onSdkReady", store.state.isTimReady);
  if (store.state.userInfo.id) {
    updateMyProfile();
    // TIM 准备好后获取聊天列表，确保未读数在所有页面都能实时显示
    store.dispatch("getChatList");
  }
}

function updateMyProfile() {
  // 修改个人标配资料
  let promise = tim.updateMyProfile({
    nick: store.state.userInfo.nick_name,
    avatar: store.state.userInfo.avatar,
  });
  promise
    .then(function (imResponse) {
      console.log(imResponse.data, "更新资料成功"); // 更新资料成功
    })
    .catch(function (imError) {
      console.warn("updateMyProfile error:", imError); // 更新资料失败的相关信息
    });
}

let roomId = 0;
// loginRep 通过 C2C 下发，同一账号的其它 Tab 也会收到。
// 记录本 Tab 正在等待的房间，兼容旧版响应中未携带 groupId 的情况。
let pendingLoginRoomId = "";
let pendingLoginTimer = null;

function setPendingLoginRoomId(value) {
  pendingLoginRoomId = normalizeRoomId(value);
  if (pendingLoginTimer) clearTimeout(pendingLoginTimer);
  pendingLoginTimer = setTimeout(() => {
    pendingLoginRoomId = "";
    pendingLoginTimer = null;
  }, 15000);
}

function clearPendingLoginRoomId() {
  pendingLoginRoomId = "";
  if (pendingLoginTimer) clearTimeout(pendingLoginTimer);
  pendingLoginTimer = null;
}

function normalizeRoomId(value) {
  return value == null ? "" : String(value);
}

function getCurrentLiveRoomId() {
  return normalizeRoomId(roomId || store.state.liveRoomData?.anchorid);
}

function getPayloadLiveRoomIds(data = {}) {
  const firstHistoryItem = data.data?.chatHistory?.[0] || {};
  return [
    data.groupId,
    data.group_id,
    data.roomId,
    data.room_id,
    data.data?.groupId,
    data.data?.group_id,
    data.data?.roomId,
    data.data?.room_id,
    data.resp?.groupId,
    data.resp?.group_id,
    data.resp?.roomId,
    data.resp?.room_id,
    firstHistoryItem.groupId,
    firstHistoryItem.group_id,
    firstHistoryItem.roomId,
    firstHistoryItem.room_id,
  ]
    .map(normalizeRoomId)
    .filter(Boolean);
}

function isForCurrentLiveRoom(message, data = {}, allowPendingLoginResponse = false) {
  const currentRoomId = getCurrentLiveRoomId();
  if (!currentRoomId) return false;

  const payloadRoomIds = getPayloadLiveRoomIds(data);
  const isGroupMessage =
    message?.conversationType === TIM.TYPES.CONV_GROUP ||
    normalizeRoomId(message?.conversationID).startsWith("GROUP");

  // 群消息：会话目标房间优先，避免 payload 里错误/无关的 group_id 误杀本房消息
  if (isGroupMessage) {
    const messageRoomIds = [
      normalizeRoomId(message.to).replace(/^GROUP/, ""),
      normalizeRoomId(message.conversationID).replace(/^GROUP/, ""),
    ].filter(Boolean);
    if (messageRoomIds.includes(currentRoomId)) return true;
    if (payloadRoomIds.length) return payloadRoomIds.includes(currentRoomId);
    return false;
  }

  // C2C（如 loginRep / 部分 sayRep）：只能依赖 payload 房间号
  if (payloadRoomIds.length) {
    return payloadRoomIds.includes(currentRoomId);
  }

  // 旧后端 loginRep 若没有回传房间 ID，只允许本 Tab 主动进房后尚未完成的首次响应。
  return allowPendingLoginResponse && pendingLoginRoomId === currentRoomId;
}

export async function timJoinGroup(room, nickname) {
  if (!tim) return;
  if (!store.state.isTimReady) {
    return new Promise((resolve, reject) => {
      const handler = () => {
        tim.off(TIM.EVENT.SDK_READY, handler);
        timJoinGroup(room, nickname).then(resolve).catch(reject);
      };
      tim.on(TIM.EVENT.SDK_READY, handler);
    });
  }
  console.log(room, nickname, "room+++++++++++++++++++++++++++++++++=");
  roomId = room;
  try {
    let imResponse = await tim.joinGroup({ groupID: String(room), type: TIM.TYPES.GRP_AVCHATROOM });
    console.log(imResponse, "imResponse");
    switch (imResponse.data.status) {
      case TIM.TYPES.JOIN_STATUS_WAIT_APPROVAL:
        console.log("等待管理员同意");
        break;
      case TIM.TYPES.JOIN_STATUS_SUCCESS:
        console.log(imResponse.data, "加群成功");
        loginRequest(room);
        timSendPing(room);
        break;
      case TIM.TYPES.JOIN_STATUS_ALREADY_IN_GROUP: // 已在群中：静默处理，不提示
        loginRequest(room);
        break;
    }
  } catch (e) {
    const msg = (e?.message || "").toLowerCase();
    const code = e?.code;
    if (msg.includes("already") || msg.includes("已在群") || code === 10014) {
      loginRequest(room);
      return;
    }
    throw e;
  }
}

function normalizeLiveChatHistoryItem(item) {
  if (!item || typeof item !== "object") return item;
  item.textColor = item.font_color;
  let props = 0;
  try {
    props = item.rich ? JSON.parse(item.rich).props_id : 0;
  } catch (e) {
    props = 0;
  }
  item.props_id = props;
  item.history = 1;
  if (props == 2) {
    item.textColor = store.state.colorfulTextColor[0];
    item.isColorful = true;
  }
  if (props == 3) {
    item.textColor = store.state.colorfulTextColor[1];
    item.isColorful = true;
  }
  if (props == 4) {
    item.textColor = store.state.colorfulTextColor[2];
    item.isColorful = true;
  }
  // 后端历史图片字段为 img_url，UI 使用 image_url
  const imgUrl = item.img_url || item.image_url || item.ImageUrl || "";
  if (imgUrl) {
    item.img_url = item.img_url || imgUrl;
    item.image_url = imgUrl;
    item.image_preview_url = item.image_preview_url || imgUrl;
  }
  if (!item.type && (item.content || item.image_url || item.img_url)) {
    item.type = "say";
  }
  return item;
}

export function timSendC2C(msg) {
  msg = JSON.stringify(msg);
  console.log(msg);
  let message = tim.createCustomMessage({
    to: "admin",
    conversationType: TIM.TYPES.CONV_C2C,
    payload: {
      data: msg,
    },
  });
  // 2. 发送消息
  let promise = tim.sendMessage(message);
  promise
    .then((imResponse) => {
      // 发送成功
      if (imResponse.code === 0) {
        console.log(imResponse, "C2C消息发送成功");
      }
    })
    .catch((imError) => {
      // 发送失败
      console.log("C2C消息发送失败", imError);
      // Vue.prototype.$message.error(imError.message);
    });
}
export function timSendGroup(roomId, msg) {
  msg = JSON.stringify(msg);
  console.log("timSendGroup", msg, roomId);
  let message = tim.createCustomMessage({
    to: String(roomId),
    conversationType: TIM.TYPES.CONV_GROUP,
    payload: {
      data: msg,
    },
  });
  // 2. 发送消息
  let promise = tim.sendMessage(message);
  promise
    .then((imResponse) => {
      // 发送成功
      if (imResponse.code === 0) {
        console.log(imResponse, "Group==消息发送成功");
      }
    })
    .catch((imError) => {
      // 发送失败
      console.log("Group==消息发送失败", imError);
      // Vue.prototype.$message.error(imError.message);
    });
}

let pingTimer = null;
function timSendPing(room) {
  if (pingTimer) {
    clearInterval(pingTimer);
  }
  let msg = { type: "ping", groupId: Number(room), device_id: localStorage.getItem("device_no") || "" };
  msg = JSON.stringify(msg);
  let message = tim.createCustomMessage({
    to: "admin",
    conversationType: TIM.TYPES.CONV_C2C,
    payload: {
      data: msg,
    },
  });
  timSendMessage(message);
  pingTimer = setInterval(() => {
    timSendMessage(message);
  }, 60000);
}

function timSendMessage(message) {
  let promise = tim.sendMessage(message);
  promise
    .then((imResponse) => {
      if (imResponse.code == 0) {
        console.log("timSendMessage ping 发送成功", JSON.parse(imResponse.data.message.payload.data));
      }
    })
    .catch((imError) => {
      console.error("timSendMessage ping 发送失败", imError);
    });
}
function loginRequest(room) {
  setPendingLoginRoomId(room);
  let msg;
  if (store.state.userInfo.id) {
    console.error(getChannel());
    msg = { type: "login", groupId: Number(room), channel: getChannel() };
  } else {
    msg = { type: "first", groupId: Number(room), channel: getChannel() };
  }
  msg = JSON.stringify(msg);
  console.log(msg);
  let message = tim.createCustomMessage({
    to: "admin",
    conversationType: TIM.TYPES.CONV_C2C,
    payload: {
      data: msg,
    },
  });
  let promise = tim.sendMessage(message);
  promise
    .then((imResponse) => {
      if (imResponse.code == 0) {
        console.log("first or login 登录成功", imResponse);
      }
    })
    .catch((imError) => {
      clearPendingLoginRoomId();
      console.error("sendMessage error:", imError);
    });
}

export function timSendGift(msg) {
  let msgJson = JSON.stringify(msg);
  let message = tim.createCustomMessage({
    to: String(roomId),
    conversationType: TIM.TYPES.CONV_GROUP,
    payload: {
      data: msgJson,
    },
  });
  // 2. 发送消息
  let promise = tim.sendMessage(message);
  promise
    .then((imResponse) => {
      // 发送成功
      if (imResponse.code == 0) {
        console.log(imResponse, "礼物发送成功");
        timSendGiftRes(msg);
      }
    })
    .catch((imError) => {
      // 发送失败
      console.log("礼物发送失败", imError);
      if (imError.code === 10152) {
        Vue.prototype.$message.error(JSON.parse(imError.message).msg);
      }
    });
}
function timSendGiftRes(msg) {
  msg.type = "giftFeed";
  msg = JSON.stringify(msg);
  let message = tim.createCustomMessage({
    to: "admin",
    conversationType: TIM.TYPES.CONV_C2C,
    payload: {
      data: msg,
    },
  });
  // 2. 发送消息
  let promise = tim.sendMessage(message);
  promise
    .then((imResponse) => {
      // 发送成功
      if (imResponse.code === 0) {
        console.log(imResponse, "礼物c2c发送成功");
      }
    })
    .catch((imError) => {
      // 发送失败
      console.log("礼物c2c发送失败", imError);
      // Vue.prototype.$message.error(imError.message);
    });
}

function timSendGuess(msg) {
  msg = JSON.stringify(msg);
  console.log(msg, "timSendGuess");
  let message = tim.createCustomMessage({
    to: "admin",
    conversationType: TIM.TYPES.CONV_C2C,
    payload: {
      data: msg,
    },
  });
  // 2. 发送消息
  let promise = tim.sendMessage(message);
  promise
    .then((imResponse) => {
      // 发送成功
      if (imResponse.code == 0) {
        let data = JSON.parse(imResponse.data.message.payload.data);
        console.log(data, "发送成功 开奖");
      }
    })
    .catch((imError) => {
      //发送失败
      console.log("sendGuess error:", imError);
      // Vue.prototype.$message.error(imError.message);
    });
}

let continuousGiftTimer = null;

/** 统一：根据匹配函数更新 store.chatList 中对应会话的 last message / unread，保证左侧列表随 IM 推送实时更新 */
function updateChatListConversation(matchFn, update) {
  const arr = store.state.chatList.map((e) => {
    if (!matchFn(e)) return e;
    const next = { ...e };
    if (update.unread_num_delta) {
      next.unread_num = (e.unread_num || 0) + update.unread_num_delta;
      // 不再在这里更新 unreadCountMap，避免与 handleNewMessage 重复计算未读数
    }
    if (update.latest_msg != null || update.latest_msg_time != null) {
      const prev = e.private_chat_latest_msg || {};
      next.private_chat_latest_msg = {
        ...prev,
        ...(update.latest_msg != null && { latest_msg: update.latest_msg }),
        ...(update.latest_msg_time != null && { latest_msg_time: update.latest_msg_time }),
      };
    }
    return next;
  });
  store.commit("changeChatList", arr);
}

/** 直播间 sayRep（含图片）写入公屏；返回是否已处理为直播间消息 */
function pushLiveRoomSayRepToMessageList(resData, message) {
  if (!resData) return false;
  const msgGroupId = normalizeRoomId(resData.group_id ?? resData.groupId);
  const liveRoomId = getCurrentLiveRoomId();
  if (!liveRoomId) return false;

  const messageTo = normalizeRoomId(message?.to).replace(/^GROUP/, "");
  const convId = normalizeRoomId(message?.conversationID).replace(/^GROUP/, "");
  const isGroupMessage =
    message?.conversationType === TIM.TYPES.CONV_GROUP ||
    normalizeRoomId(message?.conversationID).startsWith("GROUP");

  // 多开防串房：
  // 1) 群消息：必须以会话目标房间为准（只写入本 Tab 所在直播间）
  // 2) C2C：必须带有与当前房间一致的 group_id/groupId（无房间号一律丢弃，避免各 Tab 同时落屏）
  const isLiveRoomMsg = isGroupMessage
    ? messageTo === liveRoomId || convId === liveRoomId
    : !!msgGroupId && msgGroupId === liveRoomId;

  let imgUrl = String(resData.img_url || resData.image_url || "").trim();
  if (imgUrl && !/^https?:\/\//i.test(imgUrl)) imgUrl = "";

  const msgType = resData.type;
  const isTextType = msgType === 0 || msgType === "0" || msgType == null || msgType === "";
  const canWriteImage = !!imgUrl && isLiveRoomMsg;
  const canWriteText = isLiveRoomMsg && isTextType && !!resData.content;
  if (!canWriteImage && !canWriteText) return false;

  let content = resData.content || "";
  if (typeof content !== "string") content = "";
  const userId = Number(resData.uid || resData.user_id || message?.from) || 0;

  // 同用户同图去重，避免 say + sayRep 双推重复；若已有消息缺文字则补上
  if (imgUrl && Array.isArray(store.state.messageList)) {
    const existing = store.state.messageList.find(
      (item) =>
        item.type === "say" &&
        (item.img_url === imgUrl || item.image_url === imgUrl) &&
        Number(item.user_id) === userId
    );
    if (existing) {
      if (content && !existing.content) {
        store.commit("upsertMessageList", {
          ...existing,
          content,
          id: existing.id ?? resData.seq ?? message?.sequence,
          messageId: existing.messageId ?? resData.msg_id ?? message?.ID,
        });
      }
      return true;
    }
  }

  store.commit("upsertMessageList", {
    type: "say",
    // 图文并存时保留文字，不再因有图片而清空 content
    content,
    img_url: imgUrl,
    image_url: imgUrl,
    image_preview_url: imgUrl,
    from_client_name: resData.nick_name || resData.from_client_name || message?.nick || "",
    user_id: userId,
    level: Number(resData.level ?? resData.user_level ?? 0) || 0,
    vip: Number(resData.vip ?? resData.vip_level ?? 0) || 0,
    role: Number(resData.role ?? 0) || 0,
    textColor: resData.font_color || resData.textColor,
    font_color: resData.font_color,
    id: resData.seq || message?.sequence,
    messageId: resData.msg_id || message?.ID,
  });
  return true;
}

function timOnMessageReceived(event) {
  // console.log("onMessageReceived----------------", event);
  //识别是不是赛事页面
  if (window.location.pathname.indexOf("scheduleDetail") == 1) {
    store.commit("getMatchReceived", event);
    return false;
  }
  //其它页面需要的数据
  const messageList = event.data;
  //临时过滤重复消息
  // let timMsg = store.state.timMessageReceived;
  // if (timMsg.find((e) => e.ID === messageList[0].ID) !== undefined) return;
  // store.commit("changeTimMessageReceived", [...timMsg, messageList[0]]);
  messageList.forEach(async (message) => {
    // 文字消息
    if (message.type === TIM.TYPES.MSG_TEXT) {
      if (!isForCurrentLiveRoom(message)) return;
      console.log("text---------", message);
      let data = JSON.parse(message.cloudCustomData);
      data.textColor = data.props?.font_color ?? data.font_color;
      data.content = message.payload.text;
      data.from_client_name = message.nick;
      data.id = message.sequence;
      data.user_id = Number(message.from);
      data.type = "say";
      store.commit("pushMessageList", data);
      // if (store.state.messageList.length > 400) {
      //   store.state.messageList.splice(0, 100);
      // }
      // console.log(store.state.messageList, "text:: messageList");
    } else if (message.type === TIM.TYPES.MSG_IMAGE) {
      if (!isForCurrentLiveRoom(message)) return;
      let data = {};
      try {
        data = JSON.parse(message.cloudCustomData || "{}");
      } catch (error) {
        console.warn("图片消息用户信息解析失败", error);
      }
      const imageInfoList = message.payload?.imageInfoArray || [];
      const getUrl = (info) => info?.imageUrl || info?.url || "";
      const original = imageInfoList.find((info) => Number(info.type) === 0);
      const thumbnail = imageInfoList.find((info) => Number(info.type) === 2);
      const fallback = imageInfoList[0];
      const previewUrl = getUrl(original) || getUrl(fallback);
      const displayUrl = getUrl(thumbnail) || previewUrl;
      if (!displayUrl) return;

      data.type = "say";
      data.content = "";
      data.image_url = displayUrl;
      data.image_preview_url = previewUrl || displayUrl;
      data.from_client_name = message.nick;
      data.id = message.sequence;
      data.messageId = message.ID;
      data.user_id = Number(message.from);
      data.textColor = data.props?.font_color ?? data.font_color;
      store.commit("upsertMessageList", data);
    } else if (message.type === TIM.TYPES.MSG_CUSTOM) {
      let nowTime = Date.parse(new Date());
      let data;
      try {
        data = JSON.parse(message.payload.data);
      } catch (e) {
        return;
      }
      console.error(5555555,data["type"])
      switch (data["type"]) {
        case "loginRep":
          // 直播间登录响应，MESSAGE_RECEIVED 也会收到，需在此处理才能正常发消息
          if (!isForCurrentLiveRoom(message, data, true)) break;
          clearPendingLoginRoomId();
          console.error("loginRep5545555::", data);
          store.commit("changeMessageList", []);
          store.commit("changeLoginRepData", data);
          if (data.data?.guess !== "{}" && data.data?.guess) {
            store.commit("changeGuessList", JSON.parse(data.data.guess));
          }
          data.data?.chatHistory?.forEach((item) => {
            normalizeLiveChatHistoryItem(item);
          });
          if (data.data?.chatHistory) {
            store.commit("changeMessageList", data.data.chatHistory);
          }
          break;
        case "say":
          {
            if (!isForCurrentLiveRoom(message, data)) break;
            let imgUrl = String(data.img_url || data.image_url || "").trim();
            if (imgUrl && !/^https?:\/\//i.test(imgUrl)) imgUrl = "";
            if (typeof data.content !== "string") data.content = data.content ? String(data.content) : "";
            if (imgUrl) {
              data.img_url = imgUrl;
              data.image_url = imgUrl;
              data.image_preview_url = data.image_preview_url || imgUrl;
              // 图文并存时保留 content，不再清空
            } else if (!data.content) {
              break;
            }
            data.from_client_name = data.from_client_name || message.nick || "";
            data.id = data.id || message.sequence;
            data.messageId = data.messageId || message.ID;
            data.user_id = Number(data.user_id || message.from) || 0;
            data.level = Number(data.level ?? data.user_level ?? 0) || 0;
            data.vip = Number(data.vip ?? data.vip_level ?? 0) || 0;
            data.type = "say";
            // 同用户同图去重；若已有消息缺文字则补上
            if (imgUrl && Array.isArray(store.state.messageList)) {
              const existing = store.state.messageList.find(
                (item) =>
                  item.type === "say" &&
                  (item.img_url === imgUrl || item.image_url === imgUrl) &&
                  Number(item.user_id) === Number(data.user_id)
              );
              if (existing) {
                if (data.content && !existing.content) {
                  store.commit("upsertMessageList", {
                    ...existing,
                    content: data.content,
                    id: existing.id ?? data.id,
                    messageId: existing.messageId ?? data.messageId,
                  });
                }
                break;
              }
            }
            store.commit("upsertMessageList", data);
          }
          break;
        case "private_message_revoke": {
          const msgIdOrSeq = data.msg_id ?? data.seq ?? data.id;
          if (msgIdOrSeq != null && msgIdOrSeq !== "") {
            store.commit("removePrivateMessageBySeq", msgIdOrSeq);
          } else {
            store.commit("chehui", data.img_url);
          }
          break;
        }
        case "mgr_message_reply": {
          console.log(data);
          data.content = aesDecode(data.content);
          data.chatType = 2;
          let userMsg = store.state.userMessageList;
          let userChatId = store.state.userChatId || 0;
          let chatList = store.state.chatList;
          const latestContent = data.img_url ? "[图片]" : (data.content || "");
          const latestTime = data.send_time || Date.now();
          const isCurrent = userChatId === data.sender_uid;
          updateChatListConversation(
            (e) =>
              (e.mgr_chat_user_id === data.sender_uid && e.mgr_id === store.state.userInfo.id) ||
              (e.mgr_id === data.sender_uid && e.mgr_chat_user_id === store.state.userInfo.id),
            {
              latest_msg: latestContent,
              latest_msg_time: latestTime,
              unread_num_delta: isCurrent ? 0 : 1,
            }
          );
          if (userChatId !== data.sender_uid) {
            Vue.prototype.$message?.success?.("新消息");
          } else {
            let lastUserMsgTime = userMsg[userMsg.length - 1]?.send_time || 0;
            //相隔60秒添加时间线
            if (nowTime - lastUserMsgTime > 60000) {
              let timeData = {
                chatType: 3,
                time: nowTime,
                content: format(nowTime, "MM-dd hh:mm"),
              };
              store.commit("changeUserMessageList", [...userMsg, timeData]);
            }
            store.commit("changeUserMessageList", [...userMsg, data]);
          }
          let findMsg = store.state.chatList.find((e) => e.mgr_id === data.sender_uid);
          if (findMsg === undefined) {
            store.dispatch("getChatList");
          }
          break;
        }
        case "sayRep": {
          const resData = data.resp;
          if (!resData) break;
          try {
            resData.content = resData.content ? aesDecode(resData.content) : resData.content || "";
          } catch (e) {
            resData.content = resData.content || "";
          }
          resData.chatType = 2;
          resData.seq = resData.seq || message.sequence;
          let groupMsg = store.state.groupMessageList;
          let lastGroupMsgTime = groupMsg[groupMsg.length - 1]?.time || 0;
          const fanGroup = store.state.fanGroup || {};
          const currentGroupId = String(fanGroup.fans_group_id ?? fanGroup.group_id ?? "");
          const msgGroupId = String(resData.group_id ?? "");
          const isCurrentGroup = currentGroupId === msgGroupId;
          let userId = store.state.userInfo.id || 0;
          const groupMatch = (e) => String(e.fans_group_id) === msgGroupId || String(e.group_id) === msgGroupId;

          // 直播间图片/发言走 sayRep 时写入公屏
          pushLiveRoomSayRepToMessageList(resData, message);

          // 无论是否当前群、是否撤回：收到群相关推送就更新左侧列表该会话的 last message
          if (resData.type === 0 || resData.type === 7 || resData.img_url || resData.image_url) {
            const latestContent = resData.image_url || resData.img_url ? "[图片]" : (resData.content || "");
            const latestTime = resData.send_time || nowTime;
            updateChatListConversation(groupMatch, {
              latest_msg: latestContent,
              latest_msg_time: latestTime,
              unread_num_delta: isCurrentGroup ? 0 : 1,
            });
          } else if (resData.type === 2) {
            updateChatListConversation(groupMatch, {
              latest_msg: "[撤回了一条消息]",
              latest_msg_time: nowTime,
            });
          } else if (resData.type === 1) {
            const joinText = resData.uid === userId ? "您已加入群聊" : (resData.nick_name || "有人") + " 加入群聊";
            updateChatListConversation(groupMatch, {
              latest_msg: joinText,
              latest_msg_time: nowTime,
            });
          }

          if (nowTime - lastGroupMsgTime > 60000 && isCurrentGroup && resData.type === 0) {
            let timeData = {
              chatType: 3,
              send_time: nowTime,
              content: format(nowTime, "MM-dd hh:mm"),
            };
            store.commit("changeGroupMessageList", [...groupMsg, timeData]);
          }
          if (isCurrentGroup && resData.type === 0) {
            console.log("群聊提示", resData);
            store.commit("changeGroupMessageList", [...groupMsg, resData]);
          }
          if (isCurrentGroup && resData.type === 1) {
            console.log("加入群聊的提示", resData);
            resData.chatType = 3;
            if (resData.uid === userId) {
              resData.content = "您已加入群聊";
            } else {
              resData.content = resData.nick_name + " 加入群聊";
            }
            store.commit("changeGroupMessageList", [...groupMsg, resData]);
          }
          if (isCurrentGroup && resData.type === 2) {
            const seq = resData.seq;
            const msgId = resData.msg_id || resData.id;
            console.log("撤回 seq:", seq, "msg_id:", msgId, "resData:", resData);
            // 同时匹配 seq、id、msg_id 字段，确保能正确移除被撤回的消息
            let arr = groupMsg.filter((e) => {
              const eSeq = e.seq || e.id || e.msg_id;
              const matchSeq = seq && eSeq == seq;
              const matchId = msgId && (e.id == msgId || e.msg_id == msgId || e.seq == msgId);
              return !matchSeq && !matchId;
            });
            store.commit("changeGroupMessageList", arr);
          }
          if (isCurrentGroup && resData.type === 3) {
            const seq = resData.seq;
            const msgId = resData.msg_id || resData.id;
            console.log("删除 seq:", seq, "msg_id:", msgId, "resData:", resData);
            // 同时匹配 seq、id、msg_id 字段
            let arr = groupMsg.filter((e) => {
              const eSeq = e.seq || e.id || e.msg_id;
              const matchSeq = seq && eSeq == seq;
              const matchId = msgId && (e.id == msgId || e.msg_id == msgId || e.seq == msgId);
              return !matchSeq && !matchId;
            });
            store.commit("changeGroupMessageList", arr);
          }
          if (isCurrentGroup && resData.type === 4) {
            console.log("被踢出", resData);
          }
          if (isCurrentGroup && resData.type === 5) {
            console.log("被禁言", resData);
            if (resData.uid === userId) {
              store.commit("pushToast", "您已被禁言");
            }
            let filterBannedMsg = groupMsg.filter((e) => e.uid !== resData.uid);
            store.commit("changeGroupMessageList", filterBannedMsg);
          }
          if (isCurrentGroup && resData.type === 7) {
            console.log("接受图片", resData);
            store.commit("changeGroupMessageList", [...groupMsg, resData]);
          }
          break;
        }
        case "avChatSayRep": {
          const resData = data.resp;
          resData.content = aesDecode(resData.content);
          resData.chatType = 2;
          resData.seq = resData.seq || message.sequence;
          let groupMsg = store.state.groupMessageList;
          let lastGroupMsgTime = groupMsg[groupMsg.length - 1]?.time || 0;
          const fanGroup = store.state.fanGroup || {};
          const currentGroupId = String(fanGroup.fans_group_id ?? fanGroup.group_id ?? "");
          const msgGroupId = String(resData.group_id ?? "");
          const isCurrentGroup = currentGroupId === msgGroupId;
          let userId = store.state.userInfo.id || 0;
          const groupMatch = (e) => String(e.fans_group_id) === msgGroupId || String(e.group_id) === msgGroupId;

          // 无论是否当前群、是否撤回：收到群相关推送就更新左侧列表该会话的 last message
          if (resData.type === 0) {
            const latestContent = resData.image_url ? "[图片]" : (resData.content || "");
            const latestTime = resData.send_time || nowTime;
            updateChatListConversation(groupMatch, {
              latest_msg: latestContent,
              latest_msg_time: latestTime,
              unread_num_delta: isCurrentGroup ? 0 : 1,
            });
          } else if (resData.type === 2) {
            updateChatListConversation(groupMatch, {
              latest_msg: "[撤回了一条消息]",
              latest_msg_time: nowTime,
            });
          } else if (resData.type === 1) {
            const joinText = resData.uid === userId ? "您已加入群聊" : (resData.nick_name || "有人") + " 加入群聊";
            updateChatListConversation(groupMatch, {
              latest_msg: joinText,
              latest_msg_time: nowTime,
            });
          }

          if (nowTime - lastGroupMsgTime > 60000 && isCurrentGroup && resData.type === 0) {
            let timeData = {
              chatType: 3,
              send_time: nowTime,
              content: format(nowTime, "MM-dd hh:mm"),
            };
            store.commit("changeGroupMessageList", [...groupMsg, timeData]);
          }
          if (isCurrentGroup && resData.type === 0) {
            console.log("群聊提示", resData);
            store.commit("changeGroupMessageList", [...groupMsg, resData]);
          }
          if (isCurrentGroup && resData.type === 1) {
            console.log("加入群聊的提示", resData);
            resData.chatType = 3;
            if (resData.uid === userId) {
              resData.content = "您已加入群聊";
            } else {
              resData.content = resData.nick_name + " 加入群聊";
            }
            store.commit("changeGroupMessageList", [...groupMsg, resData]);
          }
          if (isCurrentGroup && resData.type === 2) {
            const seq = resData.seq;
            const msgId = resData.msg_id || resData.id;
            console.log("撤回 seq:", seq, "msg_id:", msgId, "resData:", resData);
            // 同时匹配 seq、id、msg_id 字段，确保能正确移除被撤回的消息
            let arr = groupMsg.filter((e) => {
              const eSeq = e.seq || e.id || e.msg_id;
              const matchSeq = seq && eSeq == seq;
              const matchId = msgId && (e.id == msgId || e.msg_id == msgId || e.seq == msgId);
              return !matchSeq && !matchId;
            });
            store.commit("changeGroupMessageList", arr);
          }
          if (isCurrentGroup && resData.type === 3) {
            const seq = resData.seq;
            const msgId = resData.msg_id || resData.id;
            console.log("删除 seq:", seq, "msg_id:", msgId, "resData:", resData);
            // 同时匹配 seq、id、msg_id 字段
            let arr = groupMsg.filter((e) => {
              const eSeq = e.seq || e.id || e.msg_id;
              const matchSeq = seq && eSeq == seq;
              const matchId = msgId && (e.id == msgId || e.msg_id == msgId || e.seq == msgId);
              return !matchSeq && !matchId;
            });
            store.commit("changeGroupMessageList", arr);
          }
          if (isCurrentGroup && resData.type === 4) {
            console.log("被踢出", resData);
          }
          if (isCurrentGroup && resData.type === 5) {
            console.log("被禁言", resData);
            if (resData.uid === userId) {
              store.commit("pushToast", "您已被禁言");
            }
            let filterBannedMsg = groupMsg.filter((e) => e.uid !== resData.uid);
            store.commit("changeGroupMessageList", filterBannedMsg);
          }
          if (isCurrentGroup && resData.type === 7) {
            console.log("接受图片", resData);
            store.commit("changeGroupMessageList", [...groupMsg, resData]);
          }
          break;
        }
        case "private_message_reply": {
          data.content = aesDecode(data.content);
          data.chatType = 2;
          data.time = data.send_time;
          data.sender_uid = data.groupId;
          data.avatar = data.anchor_avatar;
          data.nick_name = data.anchor_nickname;

          const isCurrentPrivate = store.state.anchorId === data.groupId && store.state.isEnterPrivateChat;
          const latestContent = data.img_url ? "[图片]" : (data.content || "");
          const latestTime = data.send_time || Date.now();
          updateChatListConversation(
            (e) => e.anchor_id === data.groupId && e.type === 1,
            {
              latest_msg: latestContent,
              latest_msg_time: latestTime,
              unread_num_delta: isCurrentPrivate ? 0 : 1,
            }
          );

          // 不在私聊页面时，刷新 chatList 以确保未读数实时更新
          if (!isCurrentPrivate) {
            debouncedGetChatList();
          }

          if (!isCurrentPrivate) {
            Vue.prototype.$message?.success?.(data.anchor_nickname + "私聊你了~");
          } else {
            let lastMsgTime = store.state.privateMessageList[store.state.privateMessageList.length - 1]?.time || 0;
            if (nowTime - lastMsgTime > 60000) {
              let timeData = {
                chatType: 3,
                time: nowTime,
                content: format(nowTime, "MM-dd hh:mm"),
              };
              store.commit("pushPrivateMessageList", timeData);
            }
            store.commit("pushPrivateMessageList", data);
            store.dispatch("readPrivateChat", Math.floor(data.send_time / 1000));
          }
          break;
        }
        case "heatRep":
          // console.log("heatRep", data);
          if (!isForCurrentLiveRoom(message, data)) break;
          store.commit("changeHot", data.val);
          store.commit("changePlayerStatus", data.status);
          break;
        case "props":
          if (!isForCurrentLiveRoom(message, data)) break;
          console.log(data, "props==================");
          if (data.props_type === 100) {
            data.isSpeaker = true;
            data.from_client_name = message.nick;
            store.commit("pushMessageList", data);
            return;
          }
          if (data.props_type === 101) {
            data.isColorful = true;
            if (data.props_id === 2) {
              data.textColor = store.state.colorfulTextColor[0];
            }
            if (data.props_id === 3) {
              data.textColor = store.state.colorfulTextColor[1];
            }
            if (data.props_id === 4) {
              data.textColor = store.state.colorfulTextColor[2];
            }
          }
          data.from_client_name = message.nick;
          data.id = message.sequence;
          data.user_id = Number(message.from);
          data.type = "say";
          store.commit("pushMessageList", data);
          break;
        case "attentAnchor":
          if (!isForCurrentLiveRoom(message, data)) break;
          store.commit("pushMessageList", data);
          store.commit("addCount");
          break;
        case "login":
          if (!isForCurrentLiveRoom(message, data)) break;
          // console.log(data, "welcome");
          if (data.vip_level > 0 || data.level > 9 || data.role > 1) {
            //最近三十条消息如果有欢迎消息，不在发送
            let isRepeatWelcome = false;
            const sliceArr = store.state.messageList.slice(-30);
            sliceArr.forEach((e) => {
              if (e.user_id === data.user_id && e.type === "login") {
                isRepeatWelcome = true;
              }
            });
            if (isRepeatWelcome) return;
            store.commit("pushMessageList", data);
          } else {
            store.commit("pushWelcomeMsg", data);
          }

          break;
        case "gift":
          if (!isForCurrentLiveRoom(message, data)) break;
          console.log("gift======>", data);
          store.commit("toggleContinuousGift", true);
          clearTimeout(continuousGiftTimer);
          const gift = store.state.gifts[`gift_${data.giftid}`];
          data.icon = gift.icon;
          data.animation = gift.animation;
          data.animat_type = gift.animat_type;
          data.user_id = data.sender;
          store.commit("changeGiftAnimation", data);
          console.log("礼物消息", store.state.giftAnimation);
          let lastMsg = store.state.messageList[store.state.messageList.length - 1];
          if (data.hit > 1) {
            if (lastMsg.sender == data.sender && lastMsg.giftid == data.giftid) {
              store.commit("changeLastHitMessageList", data.hit);
            } else {
              store.commit("pushMessageList", data);
            }
          } else {
            store.commit("pushMessageList", data);
          }
          console.log("礼物消息", gift);
          store.commit("changeContinuousGift", data);
          continuousGiftTimer = setTimeout(() => {
            store.commit("toggleContinuousGift", false);
          }, 3000);
          break;
        case "bannedUser":
          if (!isForCurrentLiveRoom(message, data)) break;
          // 删除被禁言用户的消息
          store.commit("deleteBannedMessageList", data.uid);
          if (data.mess === "") return;
          store.commit("pushMessageList", data);
          break;
        case "unbannedUser":
          if (!isForCurrentLiveRoom(message, data)) break;
          // 如果自己被解除禁言
          if (data.uid === store.state.userInfo.id) {
            store.commit("toggleDisabledSendMsg", false);
            // 解除禁言之后重置风控次数
            localStorage.removeItem("t_vio");
            localStorage.removeItem("c_vio");
            localStorage.removeItem("n_vio");
          }
          //解除禁言通知
          if (data.mess === "") return;
          store.commit("pushMessageList", data);
          break;
        case "clearChat":
          if (!isForCurrentLiveRoom(message, data)) break;
          //清除消息
          if (store.state.userInfo.id != data.user_id) {
            store.commit("deleteClearMessageList", data.chat_id);
            store.commit("changeDeleteMessageId", data.chat_id);
          }
          break;
        case "announcement":
          //直播间广告联系方式
          if (isForCurrentLiveRoom(message, data)) {
            store.commit("changeLiveAd", data.content);
            store.commit("changeLiveAdContact", JSON.parse(data.contact));
            JSON.parse(data.contact)?.forEach((e) => {
              if (e.title === "main_content") {
                store.commit("changeLiveAdContactMain", e.value);
              }
            });
          }
          break;
        case "guessSettled":
          if (!isForCurrentLiveRoom(message, data)) break;
          //竞猜开奖通知
          console.log(data, "guessSettled");
          if (store.state.userInfo.id && store.state.userInfo.id !== roomId) {
            timSendGuess({
              type: "quizPrompt",
              guess_id: data.guess_id,
              groupId: roomId,
            });
          }
          break;
        case "quiz":
          if (!isForCurrentLiveRoom(message, data)) break;
          //竞猜数据变动
          console.log(data, JSON.parse(data.message), "quiz");
          store.commit("changeGuessList", JSON.parse(data.message));
          break;
      }
    } else if (message.type === TIM.TYPES.MSG_GRP_SYS_NOTICE) {
      console.log("群系统通知", message.payload.groupProfile.to, message);
      if (Number(message.payload.groupProfile.to) === store.state.userInfo.id) {
        store.dispatch("getAnchorProfile", store.state.anchorId);
      }
    }
  });
}

function timOnMessageModified(event) {
  // console.log(event.data, "onMessageModified:::::::::::::::::");
  //识别是不是赛事页面
  if (window.location.pathname.indexOf("scheduleDetail") == 1) {
    // onMatchDetailModified(event);
    console.log("scheduleDetail----------", event.data);
    store.commit("getMatchModified", event);
    // return false;
  }
  //其它页面需要的数据
  const messageList = event.data;
  messageList.forEach((message) => {
    if (message.type === TIM.TYPES.MSG_CUSTOM) {
      let data = JSON.parse(message.payload.data);
      console.log(data, "onMessageModified:::::::::::::::::");
      if (data?.type === "sayRep" && data.resp) {
        try {
          if (data.resp.content) {
            data.resp.content = aesDecode(data.resp.content);
          }
        } catch (e) {
          // ignore decrypt error
        }
        pushLiveRoomSayRepToMessageList(data.resp, message);
      }
      switch (data["type"]) {
        case "loginRep":
          if (!isForCurrentLiveRoom(message, data, true)) break;
          clearPendingLoginRoomId();
          console.log("loginRep::", data);
          store.commit("changeMessageList", []);
          store.commit("changeLoginRepData", data);
          if (data.data?.guess && data.data.guess !== "{}") {
            store.commit("changeGuessList", JSON.parse(data.data.guess));
          }

          data.data?.chatHistory?.forEach((item) => {
            normalizeLiveChatHistoryItem(item);
          });
          if (data.data?.chatHistory) {
            store.commit("changeMessageList", data.data.chatHistory);
          }
          console.log(store.state.messageList, "MessageList");
          break;
        case "props":
          if (!isForCurrentLiveRoom(message, data)) break;
          // 炫彩文字消息
          console.error("message", message);


          // data.textColor = data.props_id;
          data.textColor = data.props?.font_color ?? data.font_color;
          //data.content = message.payload.text;
          data.from_client_name = message.nick;
          data.id = message.sequence;
          data.user_id = Number(message.from);
          data.type = "say";
          console.error("hhhhh", data);
          store.commit("pushMessageList", data);
          // store.commit("pushMessageList", data);
          break;
        case "gift":
          if (!isForCurrentLiveRoom(message, data)) break;
          // 礼物消息
          const gift = store.state.gifts[`gift_${data.giftid}`];
          store.commit("toggleContinuousGift", true);
          clearTimeout(continuousGiftTimer);
          data.icon = gift.icon;
          data.animation = gift.animation;
          data.animat_type = gift.animat_type;
          data.user_id = data.sender;
          store.commit("changeGiftAnimation", data);
          console.log("礼物消息", store.state.giftAnimation);
          let lastMsg = store.state.messageList[store.state.messageList.length - 1];
          if (data.hit > 1) {
            if (lastMsg.sender == data.sender && lastMsg.giftid == data.giftid) {
              store.commit("changeLastHitMessageList", data.hit);
            } else {
              store.commit("pushMessageList", data);
            }
          } else {
            store.commit("pushMessageList", data);
          }
          store.commit("changeContinuousGift", data);
          continuousGiftTimer = setTimeout(() => {
            store.commit("toggleContinuousGift", false);
          }, 3000);
          break;
        case "giftFeedRep":
          let keyValue = {
            ...JSON.parse(localStorage.getItem("userInfo")),
            diamond: data.bal.diamond,
            gold: data.bal.gold,
          };
          localStorage.setItem("userInfo", JSON.stringify(keyValue));
          store.commit("changeUserInfo", {
            ...store.state.userInfo,
            diamond: data.bal.diamond,
            gold: data.bal.gold,
          });
          break;
        case "clearRep":
          //删除消息请求
          store.commit("deleteClearMessageList", data.chat_id);
          store.commit("changeDeleteMessageId", data.chat_id);
          store.commit("pushToast", "删除发言成功");
          break;
        case "quizPrompt":
          //开奖消息
          console.log(data, "quizPrompt");
          store.commit("changeQuizPrompt", data);
          break;
      }
    } else if (message.type === TIM.TYPES.MSG_TEXT) {
      if (!isForCurrentLiveRoom(message)) return;
      console.log("text---------", message);
      let data = JSON.parse(message.cloudCustomData);
      data.textColor = data.props?.font_color ?? data.font_color;
      data.content = message.payload.text;
      data.from_client_name = message.nick;
      data.id = message.sequence;
      data.messageId = message.ID;
      data.user_id = Number(message.from);
      data.type = "say";
      store.commit("upsertMessageList", data);
    }
  });
}
